import { useMemo, useReducer } from 'react';

import {
  getFirstTestForGroup,
  getFirstTestForGroup_templated,
  getFirstTestGroupForState,
  getFirstTestGroupForState_templated,
  getQuestionTypes,
  getQuestionTypes_templated,
  getTestHasUploadOptions,
  getTestHasUploadOptions_templated,
} from '../components/dynamicPages/CreatePage/TestPrep.Utils';
import { UserAccessBadge } from './useAccessBadge';

export const LS_KEY_TEST_PREP_STATE = '@curipod_v1_createWithAI-TestPrepState';

export interface TestPrepState {
  selectedState: string; // e.g. "TX"
  selectedTestCategory?: string; // e.g. "STAAR"
  selectedTestCategoryDropdown?: string;
  selectedTest?: string; // e.g. "STAAR RLA"
  selectedQuestionType?: string;
  isUpload?: boolean;
  lessonVariant?: string;
  selectedPage: number;
  hasUploadOptions?: boolean;
}

export type TestPrepAction =
  | { type: 'SET_SELECTED_STATE'; payload: string }
  | { type: 'SET_SELECTED_TEST_CATEGORY'; payload: string }
  | { type: 'SET_SELECTED_TEST_CATEGORY_DROPDOWN'; payload: string | undefined }
  | { type: 'SET_SELECTED_TEST'; payload: string }
  | { type: 'SET_SELECTED_QUESTION_TYPE'; payload: string | undefined }
  | { type: 'SET_IS_UPLOAD'; payload: boolean | undefined }
  | { type: 'SET_LESSON_VARIANT'; payload: string | undefined }
  | { type: 'SET_SELECTED_PAGE'; payload: number }
  | { type: 'GO_BACK'; payload: number }
  | { type: 'RESET' };

function testPrepReducer(state: TestPrepState, action: TestPrepAction): TestPrepState {
  switch (action.type) {
    case 'SET_SELECTED_STATE':
      return { ...state, selectedState: action.payload };

    case 'SET_SELECTED_TEST_CATEGORY':
      const firstTest = getFirstTestForGroup(state.selectedState, action.payload);
      const firstQuestionType = firstTest
        ? getQuestionTypes({
            testId: firstTest,
            generators: undefined,
            userAccessBadge: undefined,
          })[0].value
        : undefined;
      const hasUploadOptions = firstTest
        ? getTestHasUploadOptions(firstTest, firstQuestionType)
        : undefined;

      return {
        ...state,
        selectedTestCategory: action.payload,
        selectedTestCategoryDropdown: action.payload,
        selectedTest: firstTest,
        selectedQuestionType: firstQuestionType,
        hasUploadOptions,
      };

    case 'SET_SELECTED_TEST_CATEGORY_DROPDOWN':
      return { ...state, selectedTestCategoryDropdown: action.payload };

    case 'SET_SELECTED_TEST':
      const firstTestCategory = getFirstTestGroupForState(state.selectedState);
      const firstQuestionTypeForTest = getQuestionTypes({
        testId: action.payload,
        generators: undefined,
        userAccessBadge: undefined,
      })[0].value;
      const hasUploadOptionsForTest = getTestHasUploadOptions(
        action.payload,
        firstQuestionTypeForTest,
      );
      return {
        ...state,
        selectedTest: action.payload,
        selectedTestCategory: firstTestCategory,
        selectedQuestionType: firstQuestionTypeForTest,
        hasUploadOptions: hasUploadOptionsForTest,
      };

    case 'SET_SELECTED_QUESTION_TYPE':
      const hasUploadOptionsForQuestionType = state.selectedTest
        ? getTestHasUploadOptions(state.selectedTest, action.payload)
        : undefined;

      return {
        ...state,
        selectedQuestionType: action.payload,
        hasUploadOptions: hasUploadOptionsForQuestionType,
      };

    case 'SET_IS_UPLOAD':
      return { ...state, isUpload: action.payload };

    case 'SET_LESSON_VARIANT':
      return { ...state, lessonVariant: action.payload };

    case 'SET_SELECTED_PAGE':
      return { ...state, selectedPage: action.payload, isUpload: undefined };

    case 'GO_BACK':
      return {
        ...state,
        isUpload: undefined,
        lessonVariant: undefined,
        selectedPage: state.hasUploadOptions ? action.payload : 1,
      };

    case 'RESET':
      return initTestPrepState(state.selectedState);

    default:
      return state;
  }
}
function templatedTestPrepReducer(
  state: TestPrepState,
  action: TestPrepAction,
): TestPrepState {
  switch (action.type) {
    case 'SET_SELECTED_STATE':
      return { ...state, selectedState: action.payload };

    case 'SET_SELECTED_TEST_CATEGORY':
      const firstTest = getFirstTestForGroup_templated(
        state.selectedState,
        action.payload,
      );
      const firstQuestionType = firstTest
        ? getQuestionTypes_templated({
            testId: firstTest,
            generators: undefined,
            userAccessBadge: undefined,
          })[0].value
        : undefined;
      const hasUploadOptions = firstTest
        ? getTestHasUploadOptions_templated(firstTest, firstQuestionType)
        : undefined;

      return {
        ...state,
        selectedTestCategory: action.payload,
        selectedTestCategoryDropdown: action.payload,
        selectedTest: firstTest,
        selectedQuestionType: firstQuestionType,
        hasUploadOptions,
      };

    case 'SET_SELECTED_TEST_CATEGORY_DROPDOWN':
      return { ...state, selectedTestCategoryDropdown: action.payload };

    case 'SET_SELECTED_TEST':
      const firstTestCategory = getFirstTestGroupForState_templated(state.selectedState);
      const firstQuestionTypeForTest = getQuestionTypes_templated({
        testId: action.payload,
        generators: undefined,
        userAccessBadge: undefined,
      })[0].value;
      const hasUploadOptionsForTest = getTestHasUploadOptions_templated(
        action.payload,
        firstQuestionTypeForTest,
      );
      return {
        ...state,
        selectedTest: action.payload,
        selectedTestCategory: firstTestCategory,
        selectedQuestionType: firstQuestionTypeForTest,
        hasUploadOptions: hasUploadOptionsForTest,
      };

    case 'SET_SELECTED_QUESTION_TYPE':
      const hasUploadOptionsForQuestionType = state.selectedTest
        ? getTestHasUploadOptions_templated(state.selectedTest, action.payload)
        : undefined;

      return {
        ...state,
        selectedQuestionType: action.payload,
        hasUploadOptions: hasUploadOptionsForQuestionType,
      };

    case 'SET_IS_UPLOAD':
      return { ...state, isUpload: action.payload };

    case 'SET_LESSON_VARIANT':
      return { ...state, lessonVariant: action.payload };

    case 'SET_SELECTED_PAGE':
      return { ...state, selectedPage: action.payload, isUpload: undefined };

    case 'GO_BACK':
      return {
        ...state,
        isUpload: undefined,
        lessonVariant: undefined,
        selectedPage: state.hasUploadOptions ? action.payload : 1,
      };

    case 'RESET':
      return initTestPrepStateTemplated(state.selectedState);

    default:
      return state;
  }
}

/**
 * Reads LocalStorage & returns an initial `TestPrepState`.
 * Also applies fallback logic for when nothing is in LocalStorage,
 * e.g., defaulting to `presetState` or 'TX'.
 */
function initTestPrepState(presetState?: string): TestPrepState {
  const localTestPrepState =
    typeof window !== 'undefined' ? localStorage.getItem(LS_KEY_TEST_PREP_STATE) : null;

  const parsedUSState = localTestPrepState
    ? JSON.parse(localTestPrepState).selectedState
    : null;

  const selectedState = parsedUSState ? parsedUSState : presetState ? presetState : 'TX';

  const parsedTestPrepCategory = localTestPrepState
    ? JSON.parse(localTestPrepState).selectedTestCategory
    : null;

  const selectedTestCategory = parsedTestPrepCategory
    ? parsedTestPrepCategory
    : getFirstTestGroupForState(selectedState); // e.g. "STAAR"

  const parsedTest = localTestPrepState
    ? JSON.parse(localTestPrepState).selectedTest
    : null;

  const selectedTest = parsedTest
    ? parsedTest
    : getFirstTestForGroup(selectedState, selectedTestCategory);
  const selectedQuestionType = selectedTest
    ? getQuestionTypes({
        testId: selectedTest,
        generators: undefined,
        userAccessBadge: undefined,
      })[0].value
    : undefined;
  const hasUploadOptions = selectedQuestionType
    ? getTestHasUploadOptions(selectedTest, selectedQuestionType)
    : undefined;

  return {
    selectedState,
    selectedTestCategory,
    selectedTestCategoryDropdown: selectedTestCategory, // default to the same as selectedTestCategory
    selectedTest,
    selectedQuestionType,
    isUpload: undefined,
    lessonVariant: undefined,
    selectedPage: 1,
    hasUploadOptions,
  };
}

function initTestPrepStateTemplated(presetState?: string): TestPrepState {
  const localTestPrepState =
    typeof window !== 'undefined' ? localStorage.getItem(LS_KEY_TEST_PREP_STATE) : null;

  const parsedUSState = localTestPrepState
    ? JSON.parse(localTestPrepState).selectedState
    : null;

  const selectedState = parsedUSState ? parsedUSState : presetState ? presetState : 'TX';

  const parsedTestPrepCategory = localTestPrepState
    ? JSON.parse(localTestPrepState).selectedTestCategory
    : null;

  const selectedTestCategory = parsedTestPrepCategory
    ? parsedTestPrepCategory
    : getFirstTestGroupForState_templated(selectedState); // e.g. "STAAR"

  const parsedTest = localTestPrepState
    ? JSON.parse(localTestPrepState).selectedTest
    : null;

  const selectedTest = parsedTest
    ? parsedTest
    : getFirstTestForGroup_templated(selectedState, selectedTestCategory);
  const selectedQuestionType = selectedTest
    ? getQuestionTypes_templated({
        testId: selectedTest,
        generators: undefined,
        userAccessBadge: undefined,
      })[0].value
    : undefined;
  const hasUploadOptions = selectedQuestionType
    ? getTestHasUploadOptions_templated(selectedTest, selectedQuestionType)
    : undefined;

  return {
    selectedState,
    selectedTestCategory,
    selectedTestCategoryDropdown: selectedTestCategory, // default to the same as selectedTestCategory
    selectedTest,
    selectedQuestionType,
    isUpload: undefined,
    lessonVariant: undefined,
    selectedPage: 1,
    hasUploadOptions,
  };
}

export function useTestPrepState(
  userAccessBadge?: UserAccessBadge,
  isTestPrepTemplatesEnabled?: boolean,
) {
  const presetState = useMemo(() => {
    return userAccessBadge?.organization?.region;
  }, [userAccessBadge]);

  // Initialize the reducer
  const [testPrepState, dispatch] = useReducer(
    isTestPrepTemplatesEnabled ? templatedTestPrepReducer : testPrepReducer,
    presetState,
    isTestPrepTemplatesEnabled ? initTestPrepStateTemplated : initTestPrepState,
  );

  // Return
  return { testPrepState, dispatch };
}
